import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getOrder } from "../../services/api/portal";
import { PortalOrder } from "../../domain/portal";
import { readPortalSession, writePortalSession } from "../../lib/storage/portalSession";
import { normalizeError } from "../../services/http/errors";

export default function PortalChooseItems() {
  const { portalSlug = "" } = useParams();
  const nav = useNavigate();
  const session = useMemo(() => readPortalSession(), []);
  const [order, setOrder] = useState<PortalOrder | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, msg: "" });

  useEffect(() => {
    getOrder(portalSlug)
      .then((o) => {
        setOrder(o);
        setSelected(session?.selectedItemIds || []);
      })
      .catch((e) => {
        const err = normalizeError(e);
        setToast({ open: true, msg: err.message });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalSlug]);

  if (!session || session.portalSlug !== portalSlug) return <Navigate to={`/r/${portalSlug}`} replace />;
  if (!session.verified) return <Navigate to={`/r/${portalSlug}/verify`} replace />;

  const toggle = (id: string) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const next = () => {
    writePortalSession({ ...session, selectedItemIds: selected });
    nav(`/r/${portalSlug}/resolution`);
  };

  return (
    <>
      <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast({ open: false, msg: "" })} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity="error" onClose={() => setToast({ open: false, msg: "" })} variant="filled">
          {toast.msg}
        </Alert>
      </Snackbar>

      <Card sx={{ maxWidth: 560, width: "100%" }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={3}>
            <IconButton onClick={() => nav(-1)} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6">Choose Items</Typography>
          </Stack>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {order?.items.map((it) => {
                const on = selected.includes(it.id);
                return (
                  <Card
                    key={it.id}
                    variant="outlined"
                    sx={{
                      cursor: "pointer",
                      borderColor: on ? "primary.main" : "divider",
                      borderWidth: on ? 2 : 1,
                      bgcolor: on ? "primary.light" : "transparent",
                      transition: "all 0.15s",
                      "&:hover": { borderColor: "primary.light" },
                    }}
                    onClick={() => toggle(it.id)}
                  >
                    <Stack direction="row" alignItems="center" sx={{ p: 1.5 }}>
                      <Checkbox checked={on} size="small" sx={{ mr: 1 }} tabIndex={-1} />
                      {it.imageUrl && (
                        <Box
                          component="img"
                          src={it.imageUrl}
                          alt={it.title}
                          sx={{ width: 56, height: 56, borderRadius: 1.5, objectFit: "cover", mr: 2, flexShrink: 0 }}
                        />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {it.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          SKU: {it.sku}
                        </Typography>
                        <Typography variant="body2" fontWeight={700} sx={{ mt: 0.5 }}>
                          SAR {it.priceSar}
                        </Typography>
                      </Box>
                    </Stack>
                  </Card>
                );
              })}
            </Stack>
          )}

          <Button variant="contained" size="large" fullWidth onClick={next} disabled={selected.length === 0} sx={{ mt: 3 }}>
            Continue
          </Button>
        </CardContent>
      </Card>
    </>
  );
}
